import { Component, inject, OnInit, AfterViewInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService, User } from '../../services/auth.service';
import { LoadingComponent } from '../../shared/components/loading/loading.component';

declare const google: any;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, LoadingComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit, AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  codeVerifyForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  
  isLoading = false;
  errorMessage: string | null = null;
  
  // Forgot Password Modal States
  showForgotPasswordModal = false;
  forgotPasswordStep: 'email' | 'code' | 'password' = 'email';
  forgotPasswordEmail = '';
  forgotPasswordCode = '';
  forgotPasswordLoading = false;
  forgotPasswordError: string | null = null;
  forgotPasswordSuccess: string | null = null;
  
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.initializeForm();
  }

  ngOnInit(): void {
    // Component başlatma işlemleri
  }

  ngAfterViewInit(): void {
    // Google Sign-In'i başlat
    if (this.isBrowser) {
      this.initializeGoogleSignIn();
      // Google button'ı render et
      setTimeout(() => {
        this.renderGoogleButton();
      }, 100);
    }
  }

  private initializeForm(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
    
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
    
    this.codeVerifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern(/^\d{6}$/)]]
    });
    
    this.resetPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6), this.passwordValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }
  
  // Şifre politikası validator
  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    
    const errors: ValidationErrors = {};
    
    if (!/[A-Z]/.test(value)) {
      errors['noUppercase'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['noLowercase'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['noDigit'] = true;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
      errors['noSpecial'] = true;
    }
    
    return Object.keys(errors).length ? errors : null;
  }
  
  // Şifre eşleşme validator
  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    
    if (password && confirmPassword && password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }

  login(): void {
    if (this.loginForm.invalid) {
      // Tüm alanları işaretle
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
      this.errorMessage = 'Lütfen tüm alanları doldurun.';
      return;
    }

    const formValue = this.loginForm.value;
    this.isLoading = true;
    this.errorMessage = null;

    this.authService.login(formValue.email, formValue.password).subscribe({
      next: (response) => {
        this.isLoading = false;
        
        // Kullanıcı bilgilerini sakla
        const user: User = {
          email: formValue.email,
          firstName: response.firstName,
          lastName: response.lastName,
          token: response.token
        };
        this.authService.setCurrentUser(user);
        
        // Başarılı giriş - ana sayfaya yönlendir
        this.router.navigate(['/']);
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = this.translateErrorMessage(error);
      }
    });
  }

  // Form alanlarına kolay erişim için getter'lar
  get email() { return this.loginForm.get('email'); }
  get password() { return this.loginForm.get('password'); }
  
  // Forgot Password Form getter'ları
  get forgotEmail() { return this.forgotPasswordForm.get('email'); }
  get verifyCode() { return this.codeVerifyForm.get('code'); }
  get newPassword() { return this.resetPasswordForm.get('newPassword'); }
  get confirmPassword() { return this.resetPasswordForm.get('confirmPassword'); }
  
  // Modal açma/kapama
  openForgotPasswordModal(): void {
    this.showForgotPasswordModal = true;
    this.forgotPasswordStep = 'email';
    this.forgotPasswordError = null;
    this.forgotPasswordSuccess = null;
    this.forgotPasswordCode = '';
    this.forgotPasswordForm.reset();
    this.codeVerifyForm.reset();
    this.resetPasswordForm.reset();
    
    // Eğer login formunda email varsa, onu kullan
    if (this.loginForm.get('email')?.value) {
      this.forgotPasswordForm.patchValue({
        email: this.loginForm.get('email')?.value
      });
    }
  }
  
  closeForgotPasswordModal(): void {
    this.showForgotPasswordModal = false;
    this.forgotPasswordStep = 'email';
    this.forgotPasswordError = null;
    this.forgotPasswordSuccess = null;
    this.forgotPasswordLoading = false;
    this.forgotPasswordCode = '';
  }
  
  // Adım 1: Doğrulama kodu gönder
  sendForgotPasswordCode(): void {
    if (this.forgotPasswordForm.invalid) {
      Object.keys(this.forgotPasswordForm.controls).forEach(key => {
        this.forgotPasswordForm.get(key)?.markAsTouched();
      });
      return;
    }
    
    this.forgotPasswordLoading = true;
    this.forgotPasswordError = null;
    this.forgotPasswordSuccess = null;
    this.forgotPasswordEmail = this.forgotPasswordForm.get('email')?.value;
    
    this.authService.forgotPasswordSendCode(this.forgotPasswordEmail).subscribe({
      next: (response) => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordSuccess = null;
        this.forgotPasswordStep = 'code';
      },
      error: (error) => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordError = this.translateForgotPasswordError(error);
      }
    });
  }
  
  // Adım 2: Kodu doğrula ve şifre adımına geç
  verifyCodeAndProceed(): void {
    if (this.codeVerifyForm.invalid) {
      Object.keys(this.codeVerifyForm.controls).forEach(key => {
        this.codeVerifyForm.get(key)?.markAsTouched();
      });
      return;
    }
    
    this.forgotPasswordCode = this.codeVerifyForm.get('code')?.value;
    this.forgotPasswordError = null;
    this.forgotPasswordStep = 'password';
  }
  
  // Adım 3: Yeni şifre ile sıfırla
  resetPassword(): void {
    if (this.resetPasswordForm.invalid) {
      Object.keys(this.resetPasswordForm.controls).forEach(key => {
        this.resetPasswordForm.get(key)?.markAsTouched();
      });
      return;
    }
    
    this.forgotPasswordLoading = true;
    this.forgotPasswordError = null;
    this.forgotPasswordSuccess = null;
    
    const newPassword = this.resetPasswordForm.get('newPassword')?.value;
    
    this.authService.resetPasswordWithCode(this.forgotPasswordEmail, this.forgotPasswordCode, newPassword).subscribe({
      next: (response) => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordSuccess = 'Şifreniz başarıyla sıfırlandı! Yeni şifrenizle giriş yapabilirsiniz.';
        
        // 2 saniye sonra modalı kapat
        setTimeout(() => {
          this.closeForgotPasswordModal();
        }, 2500);
      },
      error: (error) => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordError = this.translateResetPasswordError(error);
      }
    });
  }
  
  // Forgot Password hata mesajlarını çevir
  private translateForgotPasswordError(error: any): string {
    const status = error.status;
    let apiMessage = '';
    
    if (error.error) {
      if (typeof error.error === 'string') {
        apiMessage = error.error.toLowerCase();
      } else if (error.error.message) {
        apiMessage = error.error.message.toLowerCase();
      }
    }
    
    if (status === 404 || apiMessage.includes('user not found') || apiMessage.includes('not found')) {
      return 'Bu e-posta adresi ile kayıtlı bir kullanıcı bulunamadı.';
    }
    
    if (status === 429 || apiMessage.includes('too many')) {
      return 'Çok fazla istek gönderildi. Lütfen birkaç dakika bekleyip tekrar deneyin.';
    }
    
    if (status === 500 || status === 502 || status === 503) {
      return 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
    }
    
    if (status === 0 || !navigator.onLine) {
      return 'İnternet bağlantınızı kontrol edin.';
    }
    
    return 'Kod gönderilemedi. Lütfen tekrar deneyin.';
  }
  
  // Reset Password hata mesajlarını çevir
  private translateResetPasswordError(error: any): string {
    const status = error.status;
    let apiMessage = '';
    
    if (error.error) {
      if (typeof error.error === 'string') {
        apiMessage = error.error.toLowerCase();
      } else if (error.error.message) {
        apiMessage = error.error.message.toLowerCase();
      } else if (Array.isArray(error.error)) {
        // Şifre politikası hataları için
        return 'Şifre gereksinimleri karşılanmıyor. Lütfen şifre kurallarını kontrol edin.';
      }
    }
    
    if (status === 404 || apiMessage.includes('user not found')) {
      return 'Kullanıcı bulunamadı.';
    }
    
    if (status === 400) {
      if (apiMessage.includes('invalid') || apiMessage.includes('expired')) {
        return 'Geçersiz veya süresi dolmuş doğrulama kodu. Lütfen yeni kod isteyin.';
      }
      if (apiMessage.includes('password')) {
        return 'Şifre gereksinimleri karşılanmıyor. Lütfen şifre kurallarını kontrol edin.';
      }
    }
    
    if (status === 0 || !navigator.onLine) {
      return 'İnternet bağlantınızı kontrol edin.';
    }
    
    return 'Şifre sıfırlanamadı. Lütfen tekrar deneyin.';
  }
  
  // Tekrar kod gönder
  resendCode(): void {
    this.forgotPasswordStep = 'email';
    this.forgotPasswordError = null;
    this.forgotPasswordSuccess = null;
    this.forgotPasswordCode = '';
    this.codeVerifyForm.reset();
    this.resetPasswordForm.reset();
  }
  
  // Kod adımına geri dön
  goBackToCode(): void {
    this.forgotPasswordStep = 'code';
    this.forgotPasswordError = null;
    this.resetPasswordForm.reset();
  }

  // Google Sign-In başlatma
  private initializeGoogleSignIn(): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.initialize({
        client_id: '540796751930-m36rbnhi0k7dhu3c8inipse7nfvbfcd4.apps.googleusercontent.com',
        callback: (response: any) => this.handleCredentialResponse(response)
      });
    }
  }

  // Google Button'ı render et
  private renderGoogleButton(): void {
    if (typeof google !== 'undefined') {
      const buttonDiv = document.getElementById('googleSignInButton');
      if (buttonDiv) {
        google.accounts.id.renderButton(
          buttonDiv,
          { 
            theme: 'outline',
            size: 'large',
            width: buttonDiv.offsetWidth || 350,
            text: 'continue_with',
            shape: 'rectangular',
            locale: 'tr'
          }
        );
      }
    }
  }

  // Google Sign-In callback fonksiyonu
  handleCredentialResponse(response: any): void {
    const googleIdToken = response.credential;
    const payload = this.parseJwt(googleIdToken);

    // Backend'e gönder, kendi JWT'mizi al
    this.authService.googleLogin(googleIdToken).subscribe({
      next: (res) => {
        const user: User = {
          email: payload.email,
          firstName: payload.given_name || '',
          lastName: payload.family_name || '',
          token: res.token
        };
        this.authService.setCurrentUser(user);
        this.router.navigate(['/']);
      },
      error: () => {
        this.errorMessage = 'Google ile giriş başarısız. Lütfen tekrar deneyin.';
      }
    });
  }

  // JWT token'ı decode etme fonksiyonu
  private parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return {};
    }
  }

  /**
   * API hata mesajlarını Türkçe'ye çevirir
   */
  private translateErrorMessage(error: any): string {
    // API'den gelen hata mesajını al
    let apiMessage = '';
    
    if (error.error) {
      if (error.error.message) {
        apiMessage = error.error.message.toLowerCase();
      } else if (typeof error.error === 'string') {
        apiMessage = error.error.toLowerCase();
      }
    }
    
    // HTTP status koduna göre kontrol
    const status = error.status;
    
    // Hata mesajlarını Türkçe'ye çevir
    if (apiMessage.includes('invalid credentials') || 
        apiMessage.includes('wrong password') ||
        apiMessage.includes('incorrect password')) {
      return 'E-posta adresi veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.';
    }
    
    if (apiMessage.includes('user not found') || 
        apiMessage.includes('email not found') ||
        apiMessage.includes('no user') ||
        apiMessage.includes('not registered')) {
      return 'Bu e-posta adresi ile kayıtlı bir kullanıcı bulunamadı.';
    }
    
    if (apiMessage.includes('account locked') || 
        apiMessage.includes('account disabled')) {
      return 'Hesabınız kilitlenmiş. Lütfen müşteri hizmetleri ile iletişime geçin.';
    }
    
    if (apiMessage.includes('email not verified')) {
      return 'E-posta adresiniz henüz doğrulanmamış. Lütfen e-postanızı kontrol edin.';
    }
    
    if (apiMessage.includes('too many attempts') || 
        apiMessage.includes('rate limit')) {
      return 'Çok fazla başarısız giriş denemesi. Lütfen birkaç dakika bekleyip tekrar deneyin.';
    }
    
    // HTTP status kodlarına göre genel hatalar
    if (status === 401) {
      return 'E-posta adresi veya şifre hatalı.';
    }
    
    if (status === 404) {
      return 'Bu e-posta adresi ile kayıtlı bir kullanıcı bulunamadı.';
    }
    
    if (status === 429) {
      return 'Çok fazla istek gönderildi. Lütfen biraz bekleyip tekrar deneyin.';
    }
    
    if (status === 500 || status === 502 || status === 503) {
      return 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
    }
    
    if (status === 0 || !navigator.onLine) {
      return 'İnternet bağlantınızı kontrol edin.';
    }
    
    // Varsayılan hata mesajı
    return 'Giriş başarısız. Lütfen bilgilerinizi kontrol edip tekrar deneyin.';
  }

}
